import React, { useState, useEffect, useRef, useCallback } from 'react';
import swal from '@sweetalert/with-react';

import { filterArrayOfObjects, useDebouncedValue, deleteData, loadSpecificData } from '../../../js/Helpers';
import { saveDataToDatabase } from '../../../js/SaveFunctions';

import SortToggle from '../../SortToggle';
import UploadModal from '../../UploadModal';
import EditTeacherData from '../modals/edit_teacher';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEdit, faPlus, faUpload } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/free-regular-svg-icons';

const buildTeacherDraft = (teacher_info = {}) => ({
	teacher_id: teacher_info.teacher_id,
	teacher_code: teacher_info.teacher_code || '',
	name: teacher_info.name || '',
	first_name: teacher_info.first_name || '',
});

const mergeSavedTeacherIntoList = (teacher_list = [], old_teacher_id, saved_teacher) => {
	const old_teacher_id_string = `${old_teacher_id}`;
	const new_teacher_id_string = `${saved_teacher.teacher_id}`;
	const original_teacher_index = teacher_list.findIndex((teacher_info) => `${teacher_info.teacher_id}` === old_teacher_id_string);

	if(old_teacher_id_string === new_teacher_id_string)
	{
		return teacher_list.map((teacher_info) => (
			`${teacher_info.teacher_id}` === old_teacher_id_string ? saved_teacher : teacher_info
		));
	}

	const next_teacher_list = teacher_list.filter((teacher_info) => (
		`${teacher_info.teacher_id}` !== old_teacher_id_string && `${teacher_info.teacher_id}` !== new_teacher_id_string
	));

	if(original_teacher_index === -1) return [...next_teacher_list, saved_teacher];

	const safe_insert_index = Math.min(original_teacher_index, next_teacher_list.length);
	return [
		...next_teacher_list.slice(0, safe_insert_index),
		saved_teacher,
		...next_teacher_list.slice(safe_insert_index),
	];
};

const teachersMatch = (teacher_a = {}, teacher_b = {}) => {
	const normalized_a = buildTeacherDraft(teacher_a);
	const normalized_b = buildTeacherDraft(teacher_b);

	return normalized_a.teacher_code.trim() === normalized_b.teacher_code.trim()
		&& normalized_a.name.trim() === normalized_b.name.trim()
		&& normalized_a.first_name.trim() === normalized_b.first_name.trim();
};

export default function DatabaseDataTeachers({ schoolInfo, periods, databaseData, setDatabaseData, scheduleInfo, students, setStudents, courses, classrooms, dataIsLoading, blocks, handlePullNewData}) {
	const [isLoading, setIsLoading] = useState(true);
	const [handleChangeComplete, setHandleChangeComplete] = useState(false);

	const [filteredResults, setFilteredResults] = useState([]);
	const [displayResults, setDisplayResults] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const debouncedSearchValue = useDebouncedValue(searchValue, 120);
	
	const [showEditDataModal, setShowEditDataModal] = useState(false);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [currentDataID, setCurrentDataID] = useState(null);
	
	const [dontShowDeletePopup, setDontShowDeletePopup] = useState(false);
	const [editingCell, setEditingCell] = useState(null);
	const [cellDraft, setCellDraft] = useState(null);
	const [isSavingCell, setIsSavingCell] = useState(false);

	const filteredResultsRef = useRef(filteredResults);
	const activeEditorRef = useRef(null);
	const teacherCodeInputRef = useRef(null);
	const teacherLastNameInputRef = useRef(null);
	const teacherFirstNameInputRef = useRef(null);
	filteredResultsRef.current = filteredResults;
	
	const handleIsSearching = (e) => {
		if(e.keyCode !== 8)
		{
			setIsLoading(false);
			setIsSearching(true);
		}
		else
		{
			setIsLoading(true);
			setIsSearching(false);
		}
	};
	
	const handleChange = useCallback(() => {
		if(filteredResultsRef.current.length === 0 && dataIsLoading) return;
		
		const search_bar = document.getElementsByClassName('school-data-search-bar')[0];
		const search_value = search_bar ? search_bar.value : '';
		if(search_value.length > 1)
		{
			const filtered_results = filterArrayOfObjects(filteredResultsRef.current, search_value, ['name', 'first_name', 'teacher_code']);
			setDisplayResults([...filtered_results]);
		}
		else
		{
			setDisplayResults([...filteredResultsRef.current]);
		}
		
		setIsLoading(false);
		setIsSearching(false);
		setHandleChangeComplete(true);
	}, [dataIsLoading]);
	
	useEffect(() => {
		handleChange();
	}, [debouncedSearchValue, handleChange]);

	const isEditingField = useCallback((teacher_id, field) => (
		editingCell
		&& `${editingCell.teacher_id}` === `${teacher_id}`
		&& editingCell.field === field
	), [editingCell]);

	const cancelCellEdit = useCallback(() => {
		setEditingCell(null);
		setCellDraft(null);
		setIsSavingCell(false);
	}, []);

	const startCellEdit = useCallback((teacher_info, field) => {
		if(!teacher_info) return;

		setEditingCell({teacher_id: teacher_info.teacher_id, field});
		setCellDraft(buildTeacherDraft(teacher_info));
		setIsSavingCell(false);
	}, []);

	useEffect(() => {
		if(!editingCell) return;

		const input_ref_map = {
			teacher_code: teacherCodeInputRef,
			name: teacherLastNameInputRef,
			first_name: teacherFirstNameInputRef,
		};
		const input_ref = input_ref_map[editingCell.field];

		if(input_ref?.current)
		{
			input_ref.current.focus();
			if(typeof input_ref.current.select === 'function') input_ref.current.select();
		}
	}, [editingCell]);

	const updateDraftField = (field, value) => {
		setCellDraft((current_draft) => ({...current_draft, [field]: value}));
	};

	const handleSaveCell = useCallback(async (teacher_info) => {
		if(!cellDraft || !editingCell || isSavingCell) return;

		const trimmed_teacher_code = cellDraft.teacher_code ? cellDraft.teacher_code.trim() : '';
		const trimmed_last_name = cellDraft.name ? cellDraft.name.trim() : '';
		const trimmed_first_name = cellDraft.first_name ? cellDraft.first_name.trim() : '';
		const draft_to_compare = {
			...teacher_info,
			teacher_code: trimmed_teacher_code,
			name: trimmed_last_name,
			first_name: trimmed_first_name,
		};

		if(teachersMatch(teacher_info, draft_to_compare))
		{
			cancelCellEdit();
			return;
		}

		if(!trimmed_last_name || !trimmed_first_name)
		{
			swal({
				title: 'Missing Teacher First or Last Name',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: (
					<div>
						<p>You need to enter a teacher&apos;s first and last name before saving!</p>
					</div>
				)
			});
			return;
		}

		setIsSavingCell(true);

		const original_teacher_id = teacher_info.teacher_id;
		const full_teacher = await loadSpecificData({
			data_type:'teacher',
			data_id:original_teacher_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
			database:'elementary'
		});

		if(!full_teacher || full_teacher.msg)
		{
			setIsSavingCell(false);
			swal({
				title: 'Unable to Save',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: <div><p>We couldn&apos;t load this teacher&apos;s latest data. Please try again.</p></div>
			});
			return;
		}

		const teacher_to_save = {
			...full_teacher,
			teacher_id: original_teacher_id,
			teacher_code: trimmed_teacher_code,
			name: trimmed_last_name,
			first_name: trimmed_first_name,
		};

		const response_data = await saveDataToDatabase({
			school_id:schoolInfo.school_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			data_type:'teacher',
			data_to_save:teacher_to_save,
			database:'elementary'
		});

		if(!response_data?.new_data_id)
		{
			setIsSavingCell(false);
			swal({
				title: 'Unable to Save',
				icon:'warning',
				buttons: {
					cancel: {
						text: 'Okay',
						value: false,
						visible: true,
						className: 'blue-btn'
					}
				},
				content: <div><p>We couldn&apos;t save this teacher value. Please try again.</p></div>
			});
			return;
		}

		const saved_teacher_id = response_data.new_data_id;
		let saved_teacher = {
			...teacher_info,
			...teacher_to_save,
			teacher_id: saved_teacher_id,
		};

		const refreshed_teacher = await loadSpecificData({
			data_type:'teacher',
			data_id:saved_teacher_id,
			data_version_id:schoolInfo.current_data_version_id,
			schedule_version_id:schoolInfo.current_schedule_version_id,
			school_id:schoolInfo.school_id,
			database:'elementary'
		});

		if(refreshed_teacher && !refreshed_teacher.msg) saved_teacher = refreshed_teacher;

		const next_database_data = mergeSavedTeacherIntoList(databaseData, original_teacher_id, saved_teacher);
		const next_filtered_results = mergeSavedTeacherIntoList(filteredResults, original_teacher_id, saved_teacher);

		setDatabaseData(next_database_data);
		setFilteredResults(next_filtered_results);
		if(`${saved_teacher_id}` !== `${original_teacher_id}`)
		{
			setStudents((current_students) => current_students.map((student_info) => (
				`${student_info.homeroom_teacher_id || ''}` === `${original_teacher_id}`
					? {...student_info, homeroom_teacher_id: saved_teacher_id}
					: student_info
			)));
		}
		cancelCellEdit();
	}, [cellDraft, editingCell, isSavingCell, schoolInfo, databaseData, filteredResults, setDatabaseData, setStudents, cancelCellEdit]);

	const handleCellBlur = (teacher_info, field) => {
		if(!isEditingField(teacher_info.teacher_id, field) || isSavingCell) return;

		window.setTimeout(() => {
			const active_element = document.activeElement;
			const focus_stayed_inside = activeEditorRef.current && active_element ? activeEditorRef.current.contains(active_element) : false;
			if(focus_stayed_inside) return;
			handleSaveCell(teacher_info);
		}, 0);
	};

	const handleCellKeyDown = async (event, teacher_info) => {
		if(event.key === 'Enter')
		{
			event.preventDefault();
			await handleSaveCell(teacher_info);
		}
		else if(event.key === 'Escape')
		{
			event.preventDefault();
			cancelCellEdit();
		}
	};

	const handleSortClick = (sortedData) => {
		cancelCellEdit();
		setDisplayResults([...sortedData]);
	};
	
	const toggleEditData = (dataID) => {
		cancelCellEdit();
		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setCurrentDataID(dataID);
		setShowEditDataModal(true);
	};
	
	const toggleDontShow = () => {
		setDontShowDeletePopup(!dontShowDeletePopup);
	};
	
	const handeDeleteData = async (data_id) => {
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, data_type:'teacher', data_ids:[data_id], database:'elementary'};
		let delete_data_bool = true;
		
		const data_index = databaseData.findIndex((teacher) => `${teacher.teacher_id}` === `${data_id}`);
		const filter_results_index = filteredResults.findIndex((teacher) => `${teacher.teacher_id}` === `${data_id}`);
		const data_info = databaseData[data_index];
		
		if(!dontShowDeletePopup)
		{
			const options =  {
				title: 'Are you sure?',
				icon: 'warning',
				dangerMode: true,
				buttons: {
					cancel: {
						text: 'Cancel',
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: 'Yes',
						value: true,
						visible: true,
						className: 'red-btn'
					},
				},
				content: (
					<div>
						<div>
							<p>Do you really want to delete {data_info.first_name} {data_info.name}?</p>
							<p className='red-text'>This will <strong>permanently</strong> remove them from the database.</p>
						</div>
						<div className='sweet-alert-dont-show-message' onClick={() => toggleDontShow('remove')}>
							<label className="checkmark_container">Please don&rsquo;t show me this message again
								<input type="checkbox" />
								<span className="checkmark"></span>
							</label>
						</div>
					</div>
				)
			};
		
			delete_data_bool = await swal(options);
		}
		
		if(delete_data_bool)
		{
			cancelCellEdit();
			databaseData.splice(data_index, 1);
			filteredResults.splice(filter_results_index, 1);

			setDatabaseData([...databaseData]);
			setFilteredResults([...filteredResults]);
			
			deleteData(data);
		}
	};
	
	useEffect(() => {
		if(!showEditDataModal) 
		{
			setCurrentDataID(null);

			const search_bar = document.getElementsByClassName('school-data-search-bar')[0];
			const search_value = search_bar ? search_bar.value : '';
			if(search_value.length > 0) handleChange();
		}
	}, [showEditDataModal, handleChange]);
	
	useEffect(() => {
		handleChange();
	}, [filteredResults, handleChange]);

	useEffect(() => {
		if(!handleChangeComplete) setFilteredResults([...databaseData]);
	}, [databaseData, dataIsLoading, handleChangeComplete]);

	const row_style = {gridTemplateColumns:'1fr 2fr 2fr 70px'};
	
	return (
		<>
		{showEditDataModal &&
			<EditTeacherData schoolInfo={schoolInfo} dataID={currentDataID} toggleEditData={toggleEditData} scheduleInfo={scheduleInfo} periods={periods} teachers={databaseData} courses={courses} classrooms={classrooms} students={students} setStudents={setStudents} setDatabaseData={setDatabaseData} filteredResults={filteredResults} setFilteredResults={setFilteredResults} blocks={blocks}/>
		}
		{showUploadModal &&
			<UploadModal schoolInfo={schoolInfo} scheduleInfo={scheduleInfo} uploadTitle='Teachers' uploadAPIName='teacherdata' closeModal={() => setShowUploadModal(false)} handlePullNewData={handlePullNewData}/>
		}
		<div className='school-data-content-screen'>
			<h1 className='school-data-main-heading capitalize'>Teachers</h1>
			<div className='school-data-top-bar'>
				<div className='school-data-search-bar-container'>
					<input className='school-data-search-bar' placeholder='Search for teachers...' value={searchValue} onChange={(e) => setSearchValue(e.target.value)} onKeyDown={(e) => handleIsSearching(e)} />
					<FontAwesomeIcon className='school-data-search-bar-icon' icon={faSearch}/>
				</div>
				<div className='btn btn-icon-left upload-action-btn' onClick={() => {
					cancelCellEdit();
					setShowUploadModal(true);
				}}>
					<FontAwesomeIcon icon={faUpload} />
					<div>Upload</div>
				</div>
			</div>
			<div className='school-data-database-display'>
				<div className='school-data-database-row school-data-database-display-header fixed-heading-on-scroll' style={row_style}>
					<div className='school-data-database-header-col'>Code <SortToggle handleOnClick={handleSortClick} sortKey='teacher_code' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>Last Name <SortToggle handleOnClick={handleSortClick} sortKey='name' passedData={displayResults}/></div>
					<div className='school-data-database-header-col'>First Name <SortToggle handleOnClick={handleSortClick} sortKey='first_name' passedData={displayResults}/></div>
					<div className='school-data-database-add-new-container' onClick={() => toggleEditData(null)}>
						<div className='school-data-database-add-new-btn'>
							<FontAwesomeIcon className='white-text' icon={faPlus}/>
						</div>
					</div>
				</div>
				{(!handleChangeComplete && isLoading) ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
					</div>
				): isSearching ?
				(
					<div className='school-data-database-message-container'>
						<img src={require('../../../images/searching.gif')} alt='searching...' style={{height:'80px'}}/>
					</div>
				):
				(
					<>
					{(displayResults.length === 0) &&
						<div className='school-data-database-message-container'>
							<h4>Sorry, no results were found!</h4>
						</div>
					}
					{displayResults.map((data_info) => {
						return (
							<div className='school-data-database-row' style={row_style} key={data_info.teacher_id} data-teacherid={data_info.teacher_id}>
								<div
									ref={isEditingField(data_info.teacher_id, 'teacher_code') ? activeEditorRef : null}
									className={isEditingField(data_info.teacher_id, 'teacher_code') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editingCell ? () => startCellEdit(data_info, 'teacher_code') : undefined}
								>
									{isEditingField(data_info.teacher_id, 'teacher_code') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
											<input
												ref={teacherCodeInputRef}
												className='school-data-inline-edit-input'
												value={cellDraft?.teacher_code || ''}
												disabled={isSavingCell}
												onChange={(e) => updateDraftField('teacher_code', e.target.value)}
												onKeyDown={(e) => handleCellKeyDown(e, data_info)}
												onBlur={() => handleCellBlur(data_info, 'teacher_code')}
											/>
										)
									:
										<div>{data_info.teacher_code ? data_info.teacher_code : '--'}</div>
									}
								</div>
								<div
									ref={isEditingField(data_info.teacher_id, 'name') ? activeEditorRef : null}
									className={isEditingField(data_info.teacher_id, 'name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editingCell ? () => startCellEdit(data_info, 'name') : undefined}
								>
									{isEditingField(data_info.teacher_id, 'name') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
											<input
												ref={teacherLastNameInputRef}
												className='school-data-inline-edit-input'
												value={cellDraft?.name || ''}
												disabled={isSavingCell}
												onChange={(e) => updateDraftField('name', e.target.value)}
												onKeyDown={(e) => handleCellKeyDown(e, data_info)}
												onBlur={() => handleCellBlur(data_info, 'name')}
											/>
										)
									:
										<div>{data_info.name}</div>
									}
								</div>
								<div
									ref={isEditingField(data_info.teacher_id, 'first_name') ? activeEditorRef : null}
									className={isEditingField(data_info.teacher_id, 'first_name') ? 'school-data-inline-editor-container' : 'school-data-inline-editable-cell'}
									onClick={!editingCell ? () => startCellEdit(data_info, 'first_name') : undefined}
								>
									{isEditingField(data_info.teacher_id, 'first_name') ?
										(isSavingCell ?
											<div className='school-data-inline-saving-cell'>
												<img src={require('../../../images/balls.gif')} alt='saving...' className='school-data-inline-saving-gif'/>
											</div>
										:
											<input
												ref={teacherFirstNameInputRef}
												className='school-data-inline-edit-input'
												value={cellDraft?.first_name || ''}
												disabled={isSavingCell}
												onChange={(e) => updateDraftField('first_name', e.target.value)}
												onKeyDown={(e) => handleCellKeyDown(e, data_info)}
												onBlur={() => handleCellBlur(data_info, 'first_name')}
											/>
										)
									:
										<div>{data_info.first_name}</div>
									}
								</div>
								<div className='school-data-database-row-btn-container'>
									<FontAwesomeIcon className='gray-to-dark-blue-link' icon={faEdit} onClick={() => toggleEditData(data_info.teacher_id)}/>
									<FontAwesomeIcon className='gray-to-red-link' icon={faTrashAlt} onClick={() => handeDeleteData(data_info.teacher_id)}/>
								</div>
							</div>
						);
					})}
					</>
				)}
			</div>
		</div>
		</>
	);
}
